import { create } from "zustand";
import { AUTH_SOURCE } from "../../constants/authSource";
import {
  resolveCreOrganisationId,
  writeStoredCreOrganisationId,
  isCreOrganisationIdValid,
} from "../../cre/creOrganisation";
import {
  API_BASE_URLS,
  getWixReportingBaseUrl,
} from "../../config/apiBaseUrlsConstants";

const validateWixToken = async (token) => {
  try {
    const baseUrl = getWixReportingBaseUrl();
    const response = await fetch(
      `${baseUrl}/api/TokenValidation/Validate`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    const data = await response.json();
    return data.success ? data.user : null;
  } catch (error) {
    console.error("Wix token validation failed:", error);
    return null;
  }
};

export const useGlobalStore = create((set, get) => ({
  isInitialized: false,
  accessToken: null,
  _tokenBridgeInitialized: false,
  wixToken: null,
  wixUser: null,
  isWixAuth: false,
  radicalUser: null,
  authSource: null,
  creUser: null,
  creSelectedOrganisationId: null,

  clear: () => {
    set({
      isInitialized: false,
      accessToken: null,
      wixToken: null,
      wixUser: null,
      isWixAuth: false,
      radicalUser: null,
      authSource: null,
      creUser: null,
      creSelectedOrganisationId: null,
    });
  },

  setCreSelectedOrganisation: (organisationId) => {
    const { creUser } = get();
    if (!isCreOrganisationIdValid(creUser, organisationId)) return;
    if (creUser?.userId) {
      writeStoredCreOrganisationId(creUser.userId, organisationId);
    }
    set({ creSelectedOrganisationId: organisationId });
  },

  initializeWithWixToken: async (token) => {
    const user = await validateWixToken(token);
    if (user) {
      set({
        wixToken: token,
        wixUser: user,
        isWixAuth: true,
        authSource: AUTH_SOURCE.WIX,
        isInitialized: true,
        accessToken: token,
        creUser: null,
        creSelectedOrganisationId: null,
        radicalUser: null,
      });
      return true;
    }
    return false;
  },

  getWixToken: () => get().wixToken,

  initializeWithRadicalToken: (token) => {
    if (!token || typeof token !== "string") return false;
    set({
      accessToken: token.trim(),
      isWixAuth: false,
      authSource: AUTH_SOURCE.RADICAL,
      isInitialized: true,
      radicalUser: null,
      creUser: null,
      creSelectedOrganisationId: null,
      wixToken: null,
      wixUser: null,
    });
    return true;
  },

  initializeWithCreToken: async (token) => {
    try {
      const response = await fetch(
        `${API_BASE_URLS.reportingCre}/api/TokenValidation/Validate`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        return false;
      }
      const data = await response.json();
      if (data.success && data.user) {
        const creUser = data.user;
        const creSelectedOrganisationId = resolveCreOrganisationId(creUser);
        if (creUser.userId && creSelectedOrganisationId) {
          writeStoredCreOrganisationId(
            creUser.userId,
            creSelectedOrganisationId,
          );
        }
        set({
          accessToken: token,
          creUser,
          creSelectedOrganisationId,
          authSource: AUTH_SOURCE.CRE,
          isWixAuth: false,
          isInitialized: true,
          wixToken: null,
          wixUser: null,
          radicalUser: null,
        });
        return true;
      }
    } catch (error) {
      console.error("CRE token validation failed:", error);
    }
    return false;
  },

  setRadicalUser: (payload) => {
    set({ radicalUser: payload ?? null });
  },

  getAccessToken: async () => {
    const { isWixAuth, wixToken, accessToken } = get();

    if (isWixAuth && wixToken) {
      return wixToken;
    }

    if (accessToken) {
      return accessToken;
    }

    throw new Error("No access token available");
  },
}));
